import { isEmpty } from "lodash";
import { Box, Small, P, FlexItem } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";

import { TreeHeader, TreeHeaderTitle } from "src/shared/TreeView/TreeView";
import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";

/** Same layout as TreeHeaderTitle but a real anchor for copy-link / open-in-new-tab */
const TagTreeHeaderTitleLink = styled.a`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: ${({ isHidden }) => (isHidden ? "line-through" : "none")};
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  padding-left: ${(props) =>
    props.hasChildren ? "0px" : `calc(24px + ${themeGet("space.s")(props)})`};
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

const TreeText = styled(P)`
  word-break: break-word;
  z-index: 2;
  transition: ${themeGet("transition.transitionDefault")};
  color: ${(props) =>
    props.isActive
      ? themeGet("colors.primary")(props)
      : themeGet("colors.bodyText")(props)};

  ${TreeHeader}:hover & {
    color: ${(props) => themeGet("colors.primary")(props)};
  }
`;

const TagTreeHeader = ({ node, onToggle, onActivate, getTagLinkTo }) => {
  const { isTagType, isTagTypeDesc, name, tagValue, children, active } = node;

  if (isTagType) {
    const onClickTagType = (e) => {
      e.stopPropagation();
      if (!isEmpty(children)) {
        onToggle(node, !node.toggled);
      } else {
        onActivate(node);
      }
    };

    const hasChildren = Boolean(node.children);

    return (
      <TreeHeader onClick={onClickTagType} isActive={active}>
        <TreeHeaderTitle
          data-testid="treeheader-node-name"
          hasChildren={hasChildren}
          hasToggle={hasChildren}
        >
          <TreeText fontSize="1" isActive={active}>
            {name}
          </TreeText>
        </TreeHeaderTitle>
      </TreeHeader>
    );
  }

  if (isTagTypeDesc) {
    return (
      <Box ml="l" dataTestId="tag-type-desc-node">
        <RichTextContentViewer content={node.details} fontSize="1.2rem" />
      </Box>
    );
  }

  const { appliedCount } = tagValue || {};
  const hasChildren = Boolean(node.children);

  const onClickTag = (e) => {
    e.stopPropagation();
    onActivate(node);
  };

  const tagHref =
    typeof getTagLinkTo === "function" ? getTagLinkTo(node) : null;

  const tagValueTitle = (
    <>
      <TreeText fontSize="1" isActive={active}>
        {name}
      </TreeText>
      <FlexItem flex="0 0 auto">
        <Small color="subText">
          {appliedCount ? `${appliedCount} tagged` : ""}
        </Small>
      </FlexItem>
    </>
  );

  return (
    <TreeHeader onClick={onClickTag} isActive={active}>
      {tagHref ? (
        <TagTreeHeaderTitleLink
          className="treeHeaderTitle"
          data-testid="treeheader-node-name"
          href={tagHref}
          hasChildren={hasChildren}
          style={{ minWidth: 0 }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClickTag(e);
          }}
        >
          {tagValueTitle}
        </TagTreeHeaderTitleLink>
      ) : (
        <TreeHeaderTitle
          data-testid="treeheader-node-name"
          hasChildren={hasChildren}
          hasToggle={hasChildren}
        >
          {tagValueTitle}
        </TreeHeaderTitle>
      )}
    </TreeHeader>
  );
};

TagTreeHeader.propTypes = {
  node: PropTypes.object,
  onToggle: PropTypes.func,
  onActivate: PropTypes.func,
  getTagLinkTo: PropTypes.func,
};

export default TagTreeHeader;
